// 2x versions of the small world sprites (police drone, pickups, infrastructure icons). Same world size as the 1x
// art.droneTex / art.iconTex: the textures are 2 hd px per world px and the renderer shows them at scale 0.5.
import { Texture } from 'pixi.js';
import { canvas, tex } from './art';
import { K, px, disc, dk, lt, mix } from './hd-kit';
import { outline } from './hd-sprites';

const cache = new Map<string, Texture>();
const done = (key: string, c: HTMLCanvasElement) => { const t = tex(outline(c)); cache.set(key, t); return t; };

/** Police drone, 28x28 hd px (14 world px). Rotors alternate by frame; a jammed drone shows an amber eye. */
export function hdDroneTex(frame: number, jammed: boolean): Texture {
  const key = `drone${frame % 2}${jammed}`; const hit = cache.get(key); if (hit) return hit;
  const [c, g] = canvas(28, 28);
  const hull = dk(K.slate, 0.55), hullHi = K.metal, eye = jammed ? K.amber : K.red;
  // arms to the four rotors
  for (const [x, y] of [[4, 4], [20, 4], [4, 20], [20, 20]]) {
    px(g, x + 2, y + 2, 2, 2, K.metalLo);
  }
  g.strokeStyle = K.metalLo; g.lineWidth = 2; g.beginPath(); g.moveTo(5, 5); g.lineTo(23, 23); g.moveTo(23, 5); g.lineTo(5, 23); g.stroke();
  // rotors: a blurred blade disc, with the blade rotated half a turn each frame
  const r = frame % 2;
  for (const [x, y] of [[5, 5], [23, 5], [5, 23], [23, 23]]) {
    g.globalAlpha = 0.35; disc(g, x, y, 4, 4, K.metalHi); g.globalAlpha = 1;
    g.save(); g.translate(x, y); g.rotate(r ? Math.PI / 4 : -Math.PI / 4);
    px(g, -4, -1, 8, 1, K.metalHi); px(g, -1, -4, 1, 8, K.metal); g.restore();
    px(g, x - 1, y - 1, 2, 2, K.ink);
  }
  // body
  px(g, 9, 9, 10, 10, hull); px(g, 9, 9, 10, 2, hullHi); px(g, 9, 17, 10, 2, dk(hull, 0.4)); px(g, 9, 9, 2, 10, mix(hull, hullHi, 0.5));
  px(g, 11, 11, 6, 6, K.night); px(g, 12, 12, 4, 4, eye); px(g, 12, 12, 2, 2, lt(eye, 0.5));
  px(g, 13, 19, 2, 2, K.metalLo); // belly sensor
  return done(key, c);
}

export type HdIcon = 'cam' | 'fix' | 'package' | 'cash' | 'weapon' | 'ammo' | 'kiosk';
/** Icons are 20x20 hd px (10 world px). Weapon art is near-white metal so the rarity tint reads. */
export function hdIconTex(kind: HdIcon): Texture {
  const key = `icon${kind}`; const hit = cache.get(key); if (hit) return hit;
  const [c, g] = canvas(20, 20);
  switch (kind) {
    case 'cam': // body + lens, pointing +x (the sprite rotates with the camera)
      px(g, 2, 6, 12, 8, K.metal); px(g, 2, 6, 12, 2, K.metalHi); px(g, 2, 12, 12, 2, K.metalLo);
      px(g, 14, 8, 4, 4, K.night); px(g, 15, 9, 2, 2, K.cyan); px(g, 4, 8, 2, 2, K.red); break;
    case 'fix': // junction box with a hazard bolt
      px(g, 2, 2, 16, 16, dk(K.slate, 0.55)); px(g, 3, 3, 14, 14, dk(K.slate, 0.25)); px(g, 3, 3, 14, 2, K.metalHi); px(g, 3, 15, 14, 2, K.metalLo);
      for (const [x, y, w, h] of [[10, 4, 4, 2], [9, 6, 4, 2], [8, 8, 6, 2], [7, 10, 4, 2], [6, 12, 4, 2], [5, 14, 3, 1]]) px(g, x, y, w, h, K.amber);
      px(g, 8, 8, 6, 1, K.warmHi); break;
    case 'package': // taped crate
      px(g, 2, 4, 16, 14, K.wood); px(g, 2, 4, 16, 2, K.woodHi); px(g, 2, 16, 16, 2, K.woodLo); px(g, 2, 4, 2, 14, K.woodHi);
      px(g, 2, 8, 16, 2, K.amber); px(g, 8, 4, 4, 14, K.amber); px(g, 8, 4, 4, 1, K.warmHi); break;
    case 'cash': // banknote with a seal
      px(g, 2, 5, 16, 11, mix(K.cyan, K.night, 0.62)); px(g, 2, 5, 16, 2, K.cyan); px(g, 2, 14, 16, 2, dk(K.cyan, 0.85));
      px(g, 3, 6, 14, 9, dk(K.cyan, 0.55)); disc(g, 10, 10, 4, 4, lt(K.cyan, 0.7)); px(g, 9, 8, 2, 5, dk(K.cyan, 0.7)); px(g, 4, 7, 2, 1, K.white); px(g, 14, 13, 2, 1, K.white); break;
    case 'weapon': // pistol from the side, pointing +x
      px(g, 2, 7, 15, 4, K.white); px(g, 2, 7, 15, 1, lt(K.white, 0.4)); px(g, 2, 10, 15, 1, dk(K.white, 0.35));
      px(g, 4, 11, 4, 6, dk(K.white, 0.3)); px(g, 5, 11, 1, 6, K.white); px(g, 8, 11, 3, 2, dk(K.white, 0.45)); px(g, 14, 8, 3, 1, dk(K.white, 0.5)); break;
    case 'ammo': // two brass rounds
      for (const x of [4, 11]) { px(g, x, 8, 5, 10, K.warm); px(g, x, 8, 1, 10, K.warmHi); px(g, x + 4, 8, 1, 10, K.warmLo); px(g, x, 4, 5, 4, K.red); px(g, x + 1, 3, 3, 1, K.red); px(g, x, 4, 1, 3, lt(K.red, 0.35)); px(g, x, 17, 5, 1, K.warmLo); }
      break;
    case 'kiosk': // vending terminal
      px(g, 4, 2, 12, 16, dk(K.slate, 0.7)); px(g, 4, 2, 12, 1, K.metalHi); px(g, 4, 2, 1, 16, K.metal);
      px(g, 6, 4, 8, 6, K.night); px(g, 6, 4, 8, 6, mix(K.cyan, K.night, 0.35)); px(g, 6, 4, 8, 1, K.glassHi);
      px(g, 6, 12, 8, 2, K.pink); px(g, 6, 15, 3, 1, K.metalHi); px(g, 11, 15, 3, 1, K.metalHi); break;
  }
  return done(key, c);
}
